import React, { useState, useEffect } from 'react';
import css from '../NPOEnrollPage.module.css';
import arrayMutators from 'final-form-arrays';
import * as validators from '../../../util/validators';
import { camelize } from '../../../util/string';
import { Form as FinalForm } from 'react-final-form';
import TermsAndConditions from '../../AuthenticationPage/TermsAndConditions/TermsAndConditions';
import {
  Form,
  PrimaryButton,
  FieldTextInput,
  FieldCheckbox,
  FieldCheckboxGroup,
  Modal
} from '../../../components';
import { TermsOfServiceContent } from '../../../containers/TermsOfServicePage/TermsOfServicePage';
import { PrivacyPolicyContent } from '../../../containers/PrivacyPolicyPage/PrivacyPolicyPage';
import { TOS_ASSET_NAME, PRIVACY_POLICY_ASSET_NAME } from '../../AuthenticationPage/AuthenticationPage.duck';

export const NPOEnrollForm = (props) => {
  const {
    onSubmit, intl, tosFetchInProgress, tosFetchError, loading, isAuthenticated,
    tosAssetsData, onManageDisableScrolling, parentInfo,
  } = props;

  const [tosModalOpen, setTosModalOpen] = useState(false);
  const [privacyModalOpen, setPrivacyModalOpen] = useState(false);

  const emailRequired = validators.required(
    intl.formatMessage({
      id: 'SignupForm.emailRequired',
    })
  );
  const emailValid = validators.emailFormatValid(
    intl.formatMessage({
      id: 'SignupForm.emailInvalid',
    })
  );
  const passwordRequiredMessage = intl.formatMessage({
    id: 'SignupForm.passwordRequired',
  });
  const passwordMinLengthMessage = intl.formatMessage(
    {
      id: 'SignupForm.passwordTooShort',
    },
    {
      minLength: validators.PASSWORD_MIN_LENGTH,
    }
  );
  const passwordMaxLengthMessage = intl.formatMessage(
    {
      id: 'SignupForm.passwordTooLong',
    },
    {
      maxLength: validators.PASSWORD_MAX_LENGTH,
    }
  );
  const passwordMinLength = validators.minLength(
    passwordMinLengthMessage,
    validators.PASSWORD_MIN_LENGTH
  );
  const passwordMaxLength = validators.maxLength(
    passwordMaxLengthMessage,
    validators.PASSWORD_MAX_LENGTH
  );
  const passwordRequired = validators.requiredStringNoTrim(passwordRequiredMessage);
  const passwordValidators = validators.composeValidators(
    passwordRequired,
    passwordMinLength,
    passwordMaxLength
  );

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [tosModalOpen, privacyModalOpen]);

  return (
    <>
      <FinalForm
        onSubmit={onSubmit}
        mutators={{ ...arrayMutators }}
        render={fieldRenderProps => {
          const {
            handleSubmit,
            formId
          } = fieldRenderProps;

          return (
            <Form onSubmit={handleSubmit}>
              <div>
                <FieldTextInput
                  id={formId ? `${formId}.email` : 'email'}
                  type="email"
                  name="email"
                  autoComplete="email"
                  label={intl.formatMessage({
                    id: 'SignupForm.emailLabel',
                  })}
                  placeholder={intl.formatMessage({
                    id: 'SignupForm.emailPlaceholder',
                  })}
                  disabled={isAuthenticated}
                  validate={validators.composeValidators(emailRequired, emailValid)}
                />
              </div>

              <div className={css.name}>
                <FieldTextInput
                  className={css.firstNameRoot}
                  type="text"
                  id={formId ? `${formId}.fname` : 'fname'}
                  name="fname"
                  autoComplete="given-name"
                  label={intl.formatMessage({
                    id: 'SignupForm.firstNameLabel',
                  })}
                  placeholder={intl.formatMessage({
                    id: 'SignupForm.firstNamePlaceholder',
                  })}
                  validate={validators.required(
                    intl.formatMessage({
                      id: 'SignupForm.firstNameRequired',
                    })
                  )}
                />
                <FieldTextInput
                  className={css.lastNameRoot}
                  type="text"
                  id={formId ? `${formId}.lname` : 'lname'}
                  name="lname"
                  autoComplete="family-name"
                  label={intl.formatMessage({
                    id: 'SignupForm.lastNameLabel',
                  })}
                  placeholder={intl.formatMessage({
                    id: 'SignupForm.lastNamePlaceholder',
                  })}
                  validate={validators.required(
                    intl.formatMessage({
                      id: 'SignupForm.lastNameRequired',
                    })
                  )}
                />
              </div>

              <div>
                <FieldTextInput
                  className={css.formField}
                  type="text"
                  id={formId ? `${formId}.associationName` : 'associationName'}
                  name="associationName"
                  label={`Nom de l'association`}
                  placeholder={intl.formatMessage({
                    id: 'SignupForm.textPlaceholder',
                  })}
                  validate={validators.required(
                    intl.formatMessage({
                      id: 'SignupForm.textRequired',
                    })
                  )}
                />

                <FieldTextInput
                  className={css.formField}
                  type="text"
                  id={formId ? `${formId}.schoolName` : 'schoolName'}
                  name="schoolName"
                  label={`Nom de l'établissement scolaire`}
                  placeholder={intl.formatMessage({
                    id: 'SignupForm.textPlaceholder',
                  })}
                />

                <FieldTextInput
                  className={css.formField}
                  type="text"
                  id={formId ? `${formId}.address` : 'address'}
                  name="address"
                  label={'Adresse'}
                  placeholder={intl.formatMessage({
                    id: 'SignupForm.textPlaceholder',
                  })}
                  validate={validators.required(
                    intl.formatMessage({
                      id: 'SignupForm.textRequired',
                    })
                  )}
                />

                <FieldTextInput
                  className={css.formField}
                  type="text"
                  id={formId ? `${formId}.secondAddress` : 'secondAddress'}
                  name="secondAddress"
                  label={`Complément d'adresse`}
                  placeholder={intl.formatMessage({
                    id: 'SignupForm.textPlaceholder',
                  })}
                />

                <FieldTextInput
                  className={css.formField}
                  type="text"
                  id={formId ? `${formId}.zip` : 'zip'}
                  name="zip"
                  label={'Code postal'}
                  placeholder={intl.formatMessage({
                    id: 'SignupForm.textPlaceholder',
                  })}
                  validate={validators.required(
                    intl.formatMessage({
                      id: 'SignupForm.textRequired',
                    })
                  )}
                />

                <FieldTextInput
                  className={css.formField}
                  type="text"
                  id={formId ? `${formId}.city` : 'city'}
                  name="city"
                  label={'Ville'}
                  placeholder={intl.formatMessage({
                    id: 'SignupForm.textPlaceholder',
                  })}
                  validate={validators.required(
                    intl.formatMessage({
                      id: 'SignupForm.textRequired',
                    })
                  )}
                />

                <FieldTextInput
                  className={css.password}
                  type="password"
                  id={formId ? `${formId}.password` : 'password'}
                  name="password"
                  autoComplete="new-password"
                  label={intl.formatMessage({
                    id: 'SignupForm.passwordLabel',
                  })}
                  placeholder={intl.formatMessage({
                    id: 'SignupForm.passwordPlaceholder',
                  })}
                  validate={passwordValidators}
                />
              </div>

              {
                parentInfo ? (
                  <div className={css.parentInfo}>
                    <strong>Vous créez un compte affilié à:</strong> {parentInfo.associationName}
                  </div>
                ) : <></>
              }

              <div className={css.bottomWrapper}>
                <TermsAndConditions
                  onOpenTermsOfService={() => setTosModalOpen(true)}
                  onOpenPrivacyPolicy={() => setPrivacyModalOpen(true)}
                  intl={intl}
                />
                <FieldCheckbox
                  id="newsletterSubscription"
                  name="newsletterSubscription"
                  label={intl.formatMessage({
                    id: 'SignupForm.enrollToNewsletter',
                  })}
                  className={css.checkboxField}
                />
                <FieldCheckboxGroup
                  className={css.checkboxField}
                  name="provider-tos"
                  id="provider-tos"
                  optionLabelClassName={css.finePrint}
                  options={[
                    {
                      key: 'tos-and-privacy',
                      label: (
                        <span>J’accepte les <a target="_blank" className={css.termsTekos} href="/static/conditions.pdf">Conditions Générales de Service</a> de Jaïkan</span>
                      ),
                    },
                  ]}
                  validate={validators.requiredFieldArrayCheckbox('Vous devez accepter les conditions générales de service')}
                />
                <div>
                  <PrimaryButton type="submit" inProgress={loading}>
                    S'inscrire
                  </PrimaryButton>
                </div>
              </div>
            </Form>
          );
        }}
      />
      <Modal
        id="AuthenticationPage.tos"
        isOpen={tosModalOpen}
        onClose={() => setTosModalOpen(false)}
        usePortal
        onManageDisableScrolling={onManageDisableScrolling}
      >
        <div className={css.termsWrapper}>
          <TermsOfServiceContent
            inProgress={tosFetchInProgress}
            error={tosFetchError}
            data={tosAssetsData?.[camelize(TOS_ASSET_NAME)]?.data}
          />
        </div>
      </Modal>
      <Modal
        id="AuthenticationPage.privacyPolicy"
        isOpen={privacyModalOpen}
        onClose={() => setPrivacyModalOpen(false)}
        usePortal
        onManageDisableScrolling={onManageDisableScrolling}
      >
        <div className={css.privacyWrapper}>
          <PrivacyPolicyContent
            inProgress={tosFetchInProgress}
            error={tosFetchError}
            data={tosAssetsData?.[camelize(PRIVACY_POLICY_ASSET_NAME)]?.data}
          />
        </div>
      </Modal>
    </>
  );
};
